import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useContexts, useCreateContext, useUpdateContext, useDeleteContext } from "@/hooks/use-contexts";
import type { DialplanContext } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<DialplanContext>[] = [
  { key: "name", header: "Name" },
  { key: "description", header: "Description" },
  { key: "include_context", header: "Includes" },
];

export default function ContextsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useContexts();
  const createMut = useCreateContext();
  const updateMut = useUpdateContext();
  const deleteMut = useDeleteContext();

  const defaultContext = { name: "", description: null as string | null, include_context: null as string | null };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DialplanContext | null>(null);
  const [form, setForm] = useState(defaultContext);

  const openCreate = () => { setEditing(null); setForm(defaultContext); setOpen(true); };
  const openEdit = (c: DialplanContext) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description ?? null, include_context: c.include_context ?? null });
    setOpen(true);
  };
  const openDetail = (c: DialplanContext) => {
    navigate(`/config/contexts/${c.id}`);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("Context updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("Context created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (c: DialplanContext) => {
    deleteMut.mutate(c.id, { onSuccess: () => toast.success("Context deleted"), onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Trunk Contexts</h1>
          <p className="text-sm text-muted-foreground mt-1">Click a trunk context to manage its endpoints</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create Trunk Context</Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        getRowKey={(c) => c.id}
        onEdit={openEdit}
        onDelete={handleDelete}
        emptyMessage="No contexts configured."
        onRowClick={openDetail}
      />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit Trunk Context" : "Create Trunk Context"}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name * <HelpIcon text="Unique identifier. Becomes the Asterisk dialplan [context] section name. Used by endpoints to route calls. Example: incoming, outbound-sip." /></Label>
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="internal" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="description">Description <HelpIcon text="Human-readable note about this context's purpose. Not used by Asterisk. Shown in the UI for reference only." /></Label>
            <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="include_context">Include Context <HelpIcon text="Inherit all extensions from another context. The referenced context must exist. Example: 'default' includes Asterisk's built-in extensions. Commonly used to chain contexts." /></Label>
            <Input id="include_context" value={form.include_context ?? ""} onChange={(e) => updateField("include_context", e.target.value || null)} placeholder="default" />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
